// Preorder Communication Hook
// Stage 5: Refactored to use iframe-widget-core
// VAN-021: Hook for preorder-specific iframe communication

import { useCallback, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWidgetCommunication, useMessageListener } from "@8ui/iframe-widget-core";
import type { ParentMessage } from "@8ui/iframe-widget-core";
import type { RootState } from "@/core/store";

// Default values as constants to prevent new references on each render
const DEFAULT_TRUSTED_ORIGINS = ["*"];
const DEFAULT_CAPABILITIES = ["ordering", "theming", "closing"];
import {
  initializeApp,
  setParentReady,
  setTheme,
  setCustomColors,
  setSource,
  setIframeMode,
  setPendingEditOrderId,
  setReadOnly,
  setDebug,
  setMaxPreorderAmount,
} from "@/core/app/reducer";
import type {
  ErrorData,
  AppReadyData,
  ThemeChangeData,
  IframeSource,
  ParentToIframeMessage,
  ThemeChangeData as InternalThemeChangeData,
  SimpleThemeColors} from "@/types/iframe-messages";
import {
  createIframeMessage,
  isValidParentMessage
} from "@/types/iframe-messages";
import type { PaletteOptions } from "@mui/material/styles";
import { isOrderId } from "@/core/orders/guards";

interface PreorderCommunicationOptions {
  // Auto-detection options
  autoDetectIframe?: boolean;
  autoSendReady?: boolean;

  // Message handling
  onThemeChange?: (themeData: ThemeChangeData) => void;
  onParentReady?: () => void;
  onAuthCodeReceived?: (code: string) => void;
  onConfigReceived?: (config: any) => void;

  // Security options
  trustedOrigins?: string[];
  enableLogging?: boolean;

  // App configuration
  defaultSource?: IframeSource;
  capabilities?: string[];
}

/**
 * Hook for preorder-specific iframe communication
 * Extends useWidgetCommunication with preorder-specific message types
 */
export const usePreorderCommunication = (
  options: PreorderCommunicationOptions = {}
) => {
  const {
    autoDetectIframe = true,
    autoSendReady = true,
    onThemeChange,
    onParentReady,
    onAuthCodeReceived,
    onConfigReceived,
    trustedOrigins: trustedOriginsOption,
    enableLogging = false,
    defaultSource = "delivery",
    capabilities: capabilitiesOption,
  } = options;

  // Use stable references for arrays to prevent infinite re-renders
  const trustedOrigins = useMemo(
    () => trustedOriginsOption ?? DEFAULT_TRUSTED_ORIGINS,
    [trustedOriginsOption]
  );
  const capabilities = useMemo(
    () => capabilitiesOption ?? DEFAULT_CAPABILITIES,
    [capabilitiesOption]
  );

  const dispatch = useDispatch();
  const appState = useSelector((state: RootState) => state.app);
  const { source, isIframe, parentReady, brandId, pointId, theme } = appState;
  
  // Refs to track state and prevent infinite loops
  const readySentRef = useRef(false);
  const parentReadyRef = useRef(parentReady);
  const themeRef = useRef(theme);

  // Keep refs in sync with state
  parentReadyRef.current = parentReady;
  themeRef.current = theme;

  // Utility function to detect source from URL
  const getSourceFromURL = useCallback((): IframeSource | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathSegments = window.location.pathname.split("/").filter(Boolean);

    // Check URL parameters first
    const sourceParam = urlParams.get("source") as IframeSource;
    if (sourceParam && ["preorder", "qr", "delivery"].includes(sourceParam)) {
      return sourceParam;
    }

    // Check path segments
    if (pathSegments.includes("preorder")) return "preorder";
    if (pathSegments.includes("qr")) return "qr";
    if (pathSegments.includes("delivery")) return "delivery";

    // Check for QR-specific patterns
    if (urlParams.has("table") || urlParams.has("tableId")) return "qr";

    return null;
  }, []);

  // Utility function to get table ID from URL (for QR mode)
  const getTableIdFromURL = useCallback((): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("table") || urlParams.get("tableId");
  }, []);

  // Handle theme change from parent - using ref to avoid circular dependencies
  const handleThemeChange = useCallback(
    (themeData: InternalThemeChangeData) => {
      // Check if theme actually changed to prevent unnecessary updates
      // Use ref to get current value without adding to dependencies
      if (themeRef.current === themeData.theme && !themeData.colors) {
        // Theme hasn't changed and no colors provided, skip update
        return;
      }

      // Update Redux state with new theme
      if (themeRef.current !== themeData.theme) {
        dispatch(setTheme(themeData.theme));
      }
      
      if (themeData.colors) {
        // Check if colors is already PaletteOptions
        const isPaletteOptions = (
          colors: SimpleThemeColors | PaletteOptions
        ): colors is PaletteOptions => {
          return (
            "mode" in colors ||
            (typeof colors.primary === "object" && colors.primary !== null) ||
            (typeof colors.background === "object" && colors.background !== null)
          );
        };

        if (isPaletteOptions(themeData.colors)) {
          dispatch(setCustomColors(themeData.colors));
        } else {
          dispatch(setCustomColors(themeData.colors as PaletteOptions));
        }
      }

      // Call custom handler if provided
      if (onThemeChange) {
        onThemeChange(themeData);
      }
    },
    [dispatch, onThemeChange]
  );

  // Handle parent ready - using ref to avoid circular dependencies
  const handleParentReady = useCallback(() => {
    // Check if already ready to prevent unnecessary updates
    // Use ref to get current value without adding to dependencies
    if (parentReadyRef.current) {
      return;
    }
    dispatch(setParentReady(true));
    if (onParentReady) {
      onParentReady();
    }
  }, [dispatch, onParentReady]);

  // Stable callback for theme change to prevent recreation on every render
  const handleThemeChangeStable = useCallback(
    (themeData: any) => {
      // Convert widget-core ThemeData to internal ThemeChangeData
      const convertedTheme: InternalThemeChangeData = {
        theme: themeData.theme || "light",
        colors: themeData.colors,
      };
      handleThemeChange(convertedTheme);
    },
    [handleThemeChange]
  );

  // Stable callback for config received
  const handleConfigReceivedStable = useCallback(
    (config: any) => {
      // Apply relevant config values to Redux
      if (config?.isReadOnly !== undefined) {
        dispatch(setReadOnly(config.isReadOnly));
      }
      if (config?.debug !== undefined) {
        dispatch(setDebug(config.debug));
      }
      if (config?.maxPreorderAmount !== undefined) {
        dispatch(setMaxPreorderAmount(config.maxPreorderAmount));
      }
      if (onConfigReceived) {
        onConfigReceived(config);
      }
    },
    [dispatch, onConfigReceived]
  );

  // Use widget-core communication hook for basic communication
  const widget = useWidgetCommunication({
    widgetType: "preorder",
    autoSendReady: false, // We'll handle ready message manually with source
    onThemeChange: handleThemeChangeStable,
    onParentReady: handleParentReady,
    onConfigReceived: handleConfigReceivedStable,
    trustedOrigins,
    enableLogging,
  });

  // Stable handler for additional message types not covered by widget-core
  const handleAdditionalMessages = useCallback(
    (message: ParentMessage) => {
      // Widget-core handles SET_THEME, SET_CONFIG, PARENT_READY
      // We need to handle EDIT_ORDER and AUTH_CODE_TRANSFER
      if (message.type === "EDIT_ORDER" || message.type === "AUTH_CODE_TRANSFER") {
        // Validate as internal message format
        if (isValidParentMessage(message)) {
          const internalMessage = message as unknown as ParentToIframeMessage;
          
          if (internalMessage.type === "EDIT_ORDER" && internalMessage.payload) {
            const { orderId } = internalMessage.payload as { orderId: string };
            if (enableLogging) {
              console.log("usePreorderCommunication: EDIT_ORDER received, saving pendingEditOrderId:", orderId);
            }
            // Заказ кладётся заданием в Redux: грузит его useOrderRestore,
            // когда бренд станет известен. Гард «uuid или игнор» — негодное
            // значение молча не порождает запроса и СНИМАЕТ прежнее задание:
            // иначе мусорное письмо оставило бы в силе предыдущий orderId.
            dispatch(setPendingEditOrderId(isOrderId(orderId) ? orderId : null));
          } else if (internalMessage.type === "AUTH_CODE_TRANSFER" && internalMessage.payload) {
            const { code } = internalMessage.payload as { code: string };
            if (enableLogging) {
              console.log("usePreorderCommunication: Auth code received:", code);
            }
            if (code && typeof code === "string" && code.trim().length > 0) {
              if (onAuthCodeReceived) {
                try {
                  onAuthCodeReceived(code.trim());
                } catch (error) {
                  if (enableLogging) {
                    console.error("Error in onAuthCodeReceived callback:", error);
                  }
                }
              }
            }
          }
        }
      }
    },
    [dispatch, onAuthCodeReceived, enableLogging]
  );

  // Handle additional message types not covered by widget-core
  useMessageListener({
    onMessage: handleAdditionalMessages,
    trustedOrigins,
    enabled: isIframe,
  });

  // Загрузку задания ведёт useOrderRestore (hooks/useOrderRestore.ts):
  // один приёмник на оба сценария — EDIT_ORDER и восстановление черновика.

  // Detect iframe mode and initialize
  useEffect(() => {
    if (!autoDetectIframe) return;

    const isInIframe = window.parent !== window;
    const currentSource = getSourceFromURL() || defaultSource;

    if (enableLogging) {
      console.log("usePreorderCommunication: Auto-detection -", {
        isInIframe,
        currentSource,
        currentUrl: window.location.href,
      });
    }

    // Initialize app state
    dispatch(
      initializeApp({
        source: currentSource,
        isIframe: isInIframe,
        capabilities,
      })
    );
  }, [autoDetectIframe, dispatch, getSourceFromURL, defaultSource, capabilities, enableLogging]);

  // Send preorder-specific ready message with source
  const sendAppReady = useCallback(
    (data?: Partial<AppReadyData>): boolean => {
      if (!isIframe || window.parent === window) {
        if (enableLogging) {
          console.log(
            "usePreorderCommunication: Not in iframe mode, ready message not sent"
          );
        }
        return false;
      }

      try {
        const readyData: AppReadyData = {
          source: source as IframeSource,
          route: window.location.pathname,
          brandId: brandId || undefined,
          pointId: pointId || undefined,
          capabilities: ["ordering", "theming", "closing"],
          ...data,
        };

        const message = createIframeMessage("PREORDER_APP_READY", source, readyData);
        
        if (enableLogging) {
          console.log("usePreorderCommunication: Sending app ready:", message);
        }
        
        window.parent.postMessage(message, "*");
        return true;
      } catch (error) {
        console.error("usePreorderCommunication: Error sending ready:", error);
        return false;
      }
    },
    [isIframe, source, brandId, pointId, enableLogging]
  );

  // Auto-send ready message when initialized
  useEffect(() => {
    if (
      !autoSendReady ||
      !appState.initialized ||
      !appState.isIframe ||
      readySentRef.current
    ) {
      return;
    }

    const readyData: AppReadyData = {
      source: appState.source,
      route: window.location.pathname,
      brandId: appState.brandId || undefined,
      pointId: appState.pointId || undefined,
      capabilities: appState.capabilities,
    };

    // Add table ID for QR mode
    if (appState.source === "qr") {
      const tableId = getTableIdFromURL();
      if (tableId) {
        readyData.tableId = tableId;
      }
    }

    sendAppReady(readyData);
    readySentRef.current = true; // Mark as sent to prevent re-sending

    if (enableLogging) {
      console.log("usePreorderCommunication: Auto-sent ready message:", readyData);
    }
  }, [autoSendReady, appState.initialized, appState.isIframe, sendAppReady, enableLogging, getTableIdFromURL]);

  // Пара sendOrderCompleted/sendOrderUpdated снята: потребителей у неё не
  // было, а PREORDER_COMPLETED и PREORDER_UPDATED теперь шлют кнопка
  // предзаказа и useCartSync. Два пути отправки одного сообщения расходятся.

  // Send order cancelled message
  const sendCancellation = useCallback((): boolean => {
    if (!isIframe || window.parent === window) {
      return false;
    }

    try {
      const message = createIframeMessage("PREORDER_CANCELLED", source);
      window.parent.postMessage(message, "*");
      return true;
    } catch (error) {
      console.error("usePreorderCommunication: Error sending cancellation:", error);
      return false;
    }
  }, [isIframe, source]);

  // Send error message
  const sendError = useCallback(
    (error: string, code?: string, details?: any): boolean => {
      if (!isIframe || window.parent === window) {
        return false;
      }

      try {
        const errorData: ErrorData = {
          error,
          code,
          details,
        };
        const message = createIframeMessage("PREORDER_ERROR", source, errorData);
        window.parent.postMessage(message, "*");
        return true;
      } catch (err) {
        console.error("usePreorderCommunication: Error sending error:", err);
        return false;
      }
    },
    [isIframe, source]
  );

  // Request close
  const requestClose = useCallback(
    (reason: "user_action" | "error" | "completion" | "cancel" = "user_action", message?: string): boolean => {
      if (!isIframe || window.parent === window) {
        return false;
      }

      try {
        const closeData = { reason, message };
        const msg = createIframeMessage("REQUEST_CLOSE", source, closeData);
        window.parent.postMessage(msg, "*");
        return true;
      } catch (error) {
        console.error("usePreorderCommunication: Error requesting close:", error);
        return false;
      }
    },
    [isIframe, source]
  );

  // Send theme change notification
  const sendThemeChange = useCallback(
    (themeData: ThemeChangeData): boolean => {
      if (!isIframe || window.parent === window) {
        return false;
      }

      try {
        const message = createIframeMessage("THEME_CHANGED", source, themeData);
        window.parent.postMessage(message, "*");
        return true;
      } catch (error) {
        console.error("usePreorderCommunication: Error sending theme change:", error);
        return false;
      }
    },
    [isIframe, source]
  );

  // Generic send message method
  const sendMessage = useCallback(
    <T extends string>(type: T, payload?: any): boolean => {
      if (!isIframe || window.parent === window) {
        return false;
      }

      try {
        const message = createIframeMessage(type as any, source, payload);
        window.parent.postMessage(message, "*");
        return true;
      } catch (error) {
        console.error("usePreorderCommunication: Error sending message:", error);
        return false;
      }
    },
    [isIframe, source]
  );

  // Update source manually
  const updateSource = useCallback(
    (newSource: IframeSource) => {
      dispatch(setSource(newSource));
      if (enableLogging) {
        console.log("usePreorderCommunication: Source updated to:", newSource);
      }
    },
    [dispatch, enableLogging]
  );

  // Force iframe mode (useful for testing)
  const forceIframeMode = useCallback(
    (enabled: boolean) => {
      dispatch(setIframeMode(enabled));
      if (enableLogging) {
        console.log("usePreorderCommunication: Iframe mode forced to:", enabled);
      }
    },
    [dispatch, enableLogging]
  );

  return {
    // Status from widget-core
    isInitialized: widget.isInitialized,
    isParentReady: widget.isParentReady || parentReady,
    isInIframe: isIframe && window.parent !== window,
    source,
    parentReady,

    // Preorder-specific methods
    sendAppReady,
    sendCancellation,
    sendError,
    requestClose,
    sendThemeChange,
    sendMessage,

    // Utilities from widget-core
    isTrustedOrigin: widget.isTrustedOrigin,
    
    // Additional utilities
    updateSource,
    forceIframeMode,
    getSourceFromURL,
    getTableIdFromURL,
  };
};
